/**
 * Custom Personalization Fee Synchronization
 * Keeps the personalization fee product (€1) in sync with personalized jam quantities
 */

// Personalization fee variant ID
const FEE_VARIANT_ID = '54030753399132';

// Track if sync is in progress to prevent infinite loops
let syncInProgress = false;

/**
 * Disables all checkout buttons in the cart
 */
function disableCheckoutButtons() {
  const checkoutButtons = document.querySelectorAll(
    'button[name="checkout"], ' +
    'button[type="submit"][form*="cart"], ' +
    'a[href*="/checkout"], ' +
    '.cart__checkout-button, ' +
    '[data-checkout-button]'
  );
  
  checkoutButtons.forEach((/** @type {HTMLElement} */ button) => {
    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
    } else if (button instanceof HTMLAnchorElement) {
      button.style.pointerEvents = 'none';
    }
    button.dataset.syncDisabled = 'true';
    button.style.opacity = '0.5';
    button.style.cursor = 'not-allowed';
    
    // Store original text if not already stored
    if (!button.dataset.originalText) {
      button.dataset.originalText = button.textContent || '';
    }
    
    // Update button text to show syncing state
    const textElement = button.querySelector('span') || button;
    if (textElement) textElement.textContent = 'Synchronisation...';
  });
  
  console.log('[Personalization Sync] Checkout buttons disabled');
}

/**
 * Enables all checkout buttons in the cart
 */
function enableCheckoutButtons() {
  const checkoutButtons = document.querySelectorAll('[data-sync-disabled="true"]');
  
  checkoutButtons.forEach((/** @type {HTMLElement} */ button) => {
    if (button instanceof HTMLButtonElement) {
      button.disabled = false;
    } else if (button instanceof HTMLAnchorElement) {
      button.style.pointerEvents = '';
    }
    button.removeAttribute('data-sync-disabled');
    button.style.opacity = '';
    button.style.cursor = '';
    
    // Restore original text
    if (button.dataset.originalText) {
      const textElement = button.querySelector('span') || button;
      if (textElement) textElement.textContent = button.dataset.originalText;
    }
  });
  
  console.log('[Personalization Sync] Checkout buttons enabled');
}

/**
 * Syncs personalization fees with personalized items in the cart
 */
async function syncPersonalizationFees() {
  // Prevent concurrent syncs
  if (syncInProgress) {
    console.log('[Personalization Sync] Already in progress, skipping...');
    return;
  }

  try {
    syncInProgress = true;
    disableCheckoutButtons();
    console.log('[Personalization Sync] Starting sync...');

    // Fetch current cart
    const cart = await fetch('/cart.js').then(r => r.json());
    
    // Find all personalized jam lines (have _personalized property)
    const personalizedLines = cart.items.filter((/** @type {any} */ item) => 
      item.properties && item.properties._personalized === 'yes'
    );
    
    // Find all fee lines
    const feeLines = cart.items.filter((/** @type {any} */ item) => 
      item.properties && item.properties._personalization_fee === 'true'
    );

    // Calculate total quantity of personalized items
    const totalPersonalizedQty = personalizedLines.reduce((/** @type {number} */ sum, /** @type {any} */ item) => sum + item.quantity, 0);

    console.log('[Personalization Sync] Personalized items qty:', totalPersonalizedQty);
    console.log('[Personalization Sync] Current fee lines:', feeLines.length);

    // If there are no personalized items and no fee lines, nothing to sync
    if (totalPersonalizedQty === 0 && feeLines.length === 0) {
      console.log('[Personalization Sync] No personalized items or fees, nothing to sync');
      return;
    }

    /** @type {Record<string, number>} */
    const updates = {};

    if (totalPersonalizedQty === 0) {
      // No personalized items, remove all fee lines
      console.log('[Personalization Sync] No personalized items, removing fees');
      feeLines.forEach((/** @type {any} */ feeLine) => {
        updates[feeLine.key] = 0;
      });
    } else {
      // There are personalized items
      const currentFeeQty = feeLines.reduce((/** @type {number} */ sum, /** @type {any} */ item) => sum + item.quantity, 0);
      
      if (currentFeeQty !== totalPersonalizedQty) {
        console.log('[Personalization Sync] Fee qty mismatch. Need:', totalPersonalizedQty, 'Have:', currentFeeQty);
        
        if (feeLines.length === 0) {
          // No fee line exists, add one
          console.log('[Personalization Sync] Adding new fee line');
          await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({
              id: FEE_VARIANT_ID,
              quantity: totalPersonalizedQty,
              properties: {
                '_personalization_fee': 'true'
              }
            })
          });
        } else if (feeLines.length === 1) {
          // Update the single fee line
          console.log('[Personalization Sync] Updating fee line quantity to', totalPersonalizedQty);
          updates[feeLines[0].key] = totalPersonalizedQty;
        } else {
          // Multiple fee lines exist (shouldn't happen, but handle it)
          console.log('[Personalization Sync] Multiple fee lines found, consolidating');
          // Keep first, remove others
          updates[feeLines[0].key] = totalPersonalizedQty;
          for (let i = 1; i < feeLines.length; i++) {
            updates[feeLines[i].key] = 0;
          }
        }
      } else {
        console.log('[Personalization Sync] Fee quantity is correct, no changes needed');
      }
    }

    // Apply updates if any
    if (Object.keys(updates).length > 0) {
      console.log('[Personalization Sync] Applying updates:', updates);
      await fetch('/cart/update.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ updates })
      });

      // Trigger a cart update event to refresh UI
      const updatedCart = await fetch('/cart.js').then(r => r.json());
      const event = new CustomEvent('cart:update', {
        bubbles: true,
        detail: {
          resource: updatedCart,
          sourceId: 'personalization-sync',
          data: {
            itemCount: updatedCart.item_count,
            source: 'personalization-sync',
            sections: {}
          }
        }
      });
      document.dispatchEvent(event);

      console.log('[Personalization Sync] Cart updated successfully');
    }

  } catch (error) {
    console.error('[Personalization Sync] Error:', error);
  } finally {
    syncInProgress = false;
    enableCheckoutButtons();
    console.log('[Personalization Sync] Sync complete');
  }
}

/**
 * Debounce function to prevent too many rapid calls
 * @param {Function} func 
 * @param {number} wait 
 * @returns {Function}
 */
function debounce(func, wait) {
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  let timeout;
  // @ts-ignore
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      // @ts-ignore
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

// Debounced version of sync (wait 500ms after last cart change)
const debouncedSync = debounce(syncPersonalizationFees, 500);

// Listen for cart updates
document.addEventListener('cart:update', (/** @type {any} */ event) => {
  console.log('[Personalization Sync] Cart update detected');
  // Don't sync if the update came from our sync (prevent infinite loop)
  if (event.detail?.data?.source !== 'personalization-sync') {
    debouncedSync();
  }
});

// Also run on page load (for cart and checkout pages)
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', syncPersonalizationFees);
} else {
  // DOM already loaded, run immediately
  syncPersonalizationFees();
}

// Observe for dynamically added checkout buttons (like in cart drawer)
const checkoutButtonObserver = new MutationObserver((mutations) => {
  // Check if any new checkout buttons were added
  const hasNewCheckoutButtons = mutations.some(mutation => {
    return Array.from(mutation.addedNodes).some(node => {
      if (node.nodeType === 1 && node instanceof Element) { // Element node
        return (
          node.matches('button[name="checkout"]') ||
          node.matches('a[href*="/checkout"]') ||
          node.matches('.cart__checkout-button') ||
          node.querySelector('button[name="checkout"], a[href*="/checkout"], .cart__checkout-button') !== null
        );
      }
      return false;
    });
  });

  // If new checkout buttons were added and sync is in progress, disable them
  if (hasNewCheckoutButtons && syncInProgress) {
    console.log('[Personalization Sync] New checkout buttons detected, disabling...');
    disableCheckoutButtons();
  }
});

// Start observing the document for changes
checkoutButtonObserver.observe(document.body, {
  childList: true,
  subtree: true
});

// Export for use in other scripts if needed
export { syncPersonalizationFees };

